import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMercenary } from '../../../contexts/MercenaryContext';
import { useSocket } from '../../../contexts/SocketContext';
import OffseasonNotice from '../../Shared/OffseasonNotice';
import Toast from '../../Shared/Toast';
import styles from './RankedBattles.module.css';

interface Lobby {
  id: number;
  name: string;
  minLevel: number;
  maxLevel: number;
  maxPlayers: number;
  randomTeams: boolean;
  currentParticipants: number;
  pve: boolean;
  matchType: string;
  isRanked: boolean;
  bracket: string;
}

interface BracketRating {
  rating: number;
  ratingDeviation: number;
  wins: number;
  losses: number;
  totalGames: number;
  winRate: number;
  peakRating: number;
  lastRankedAt: string | null;
}

interface RatingInfo {
  mercenaryId: number;
  seasonId: number | null;
  rankedBattlesToday: number;
  rankedDailyLimit: number;
  ratings: Record<string, BracketRating>;
}

const RANKED_DAILY_LIMIT = 25;
const RANKED_MIN_LEVEL = 10;

const RankedBattles: React.FC = () => {
  const [lobbies, setLobbies] = useState<Lobby[]>([]);
  const [ratingInfo, setRatingInfo] = useState<RatingInfo | null>(null);
  const [userLobbyId, setUserLobbyId] = useState<number | null>(null);
  const [userLobbyMercenaryId, setUserLobbyMercenaryId] = useState<number | null>(null);
  const [activeBracketFilter, setActiveBracketFilter] = useState<string>('all');
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error'>('error');
  const [initialLoading, setInitialLoading] = useState(true);
  const mountedRef = useRef(true);
  const navigate = useNavigate();
  const location = useLocation();
  // Suppress auto-redirect when the user explicitly navigated back from a lobby
  const suppressRedirectRef = useRef(!!(location.state as any)?.fromLobby);
  const { mercenary } = useMercenary();
  const { socket } = useSocket();

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const fetchLobbies = async () => {
    try {
      const lobbiesResponse = await axios.get('/api/team-battle/lobbies?ranked=true');
      setLobbies(lobbiesResponse.data);
      const participationResponse = await axios.get('/api/team-battle/participation', { withCredentials: true });
      const ranked = participationResponse.data.ranked || { inLobby: false, lobbyId: null, mercenaryId: null };
      const lobbyId = ranked.inLobby ? ranked.lobbyId : null;
      setUserLobbyId(lobbyId);
      setUserLobbyMercenaryId(ranked.inLobby ? ranked.mercenaryId : null);
      // Guard: only auto-redirect if the component is still mounted and the user
      // didn't explicitly press "Back to Lobbies" (which sets fromLobby / suppressRedirect).
      if (mountedRef.current && lobbyId && !suppressRedirectRef.current) {
        navigate(`/team-battle/lobby/${lobbyId}`, { replace: true });
        return; // Don't clear initialLoading, we're navigating away
      }
    } catch (error) {
      console.error('Error fetching ranked lobbies:', error);
    }
    if (mountedRef.current) setInitialLoading(false);
  };

  const fetchRatingInfo = async () => {
    if (!mercenary?.id) return;
    try {
      const res = await axios.get(`/api/ranked/rating/${mercenary.id}`, { withCredentials: true });
      setRatingInfo(res.data);
    } catch (error) {
      console.error('Error fetching rating info:', error);
    }
  };

  // Reset the suppress flag whenever the user navigates here via the nav link
  // (new location.key, no fromLobby state).
  useEffect(() => {
    if (!(location.state as any)?.fromLobby) {
      suppressRedirectRef.current = false;
    } else {
      suppressRedirectRef.current = true;
    }
    fetchLobbies();
    fetchRatingInfo();
  }, [mercenary?.id, location.key]);

  useEffect(() => {
    if (!socket) return;
    const handleLobbyCreated = (lobby: Lobby) => {
      if (lobby.isRanked) setLobbies(prev => [...prev, lobby]);
    };
    const handleLobbyRemoved = (data: { lobbyId: number }) => {
      setLobbies(prev => prev.filter(l => l.id !== data.lobbyId));
      if (userLobbyId === data.lobbyId) setUserLobbyId(null);
    };
    const handleLobbyUpdated = (data: { lobbyId: number; currentParticipants: number }) =>
      setLobbies(prev => prev.map(l => l.id === data.lobbyId ? { ...l, currentParticipants: data.currentParticipants } : l));
    const handleBattleRedirect = ({ battleId, mercenaryId: battleMercId }: { battleId: number; mercenaryId?: number }) => {
      const activeMercenaryId = parseInt(localStorage.getItem('mercenaryId') || '0', 10);
      if (battleMercId && activeMercenaryId && battleMercId !== activeMercenaryId) return;
      // Do NOT call fetchLobbies() here. The component is about to unmount and any
      // in-flight participation API call could resolve with stale inLobby: true data,
      // causing an auto-redirect to the already-destroyed lobby ("Lobby not found").
      // Data is refreshed on next mount when the user returns to /ranked-battle.
      navigate(`/battle-result/${battleId}`);
    };

    socket.on('lobby_created', handleLobbyCreated);
    socket.on('lobby_removed', handleLobbyRemoved);
    socket.on('lobby_participant_count_update', handleLobbyUpdated);
    socket.on('redirect_to_battle', handleBattleRedirect);
    return () => {
      socket.off('lobby_created', handleLobbyCreated);
      socket.off('lobby_removed', handleLobbyRemoved);
      socket.off('lobby_participant_count_update', handleLobbyUpdated);
      socket.off('redirect_to_battle', handleBattleRedirect);
    };
  }, [socket, userLobbyId]);

  useEffect(() => {
    const interval = setInterval(() => { fetchLobbies(); fetchRatingInfo(); }, 30_000);
    return () => clearInterval(interval);
  }, [mercenary?.id]);

  const handleLeaveLobby = async () => {
    try {
      await axios.post(
        `/api/team-battle/lobbies/${userLobbyId}/leave`,
        { mercenaryId: userLobbyMercenaryId },
        { withCredentials: true }
      );
      setUserLobbyId(null);
      setUserLobbyMercenaryId(null);
      await fetchLobbies();
    } catch (error: any) {
      console.error('Error leaving the lobby:', error);
      setToastType('error');
      setToastMessage(error?.response?.data?.message ?? 'Failed to leave lobby.');
    }
  };

  const filteredLobbies = activeBracketFilter === 'all'
    ? lobbies
    : lobbies.filter(l => l.bracket === activeBracketFilter);

  const meetsMinLevel = mercenary && mercenary.level >= RANKED_MIN_LEVEL;
  const battlesToday = ratingInfo?.rankedBattlesToday ?? 0;
  const atDailyLimit = battlesToday >= RANKED_DAILY_LIMIT;

  const getRatingDisplay = (bracket: string) => {
    if (!ratingInfo?.ratings?.[bracket]) return null;
    const r = ratingInfo.ratings[bracket];
    return r;
  };

  if (initialLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', color: '#a0a0b8' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>⚔</div>
          <div>Loading ranked lobbies...</div>
        </div>
      </div>
    );
  }

  return (
    <OffseasonNotice feature="ranked battles">
      <div className={styles.rankedContainer}>
        {toastMessage && (
          <Toast message={toastMessage} type={toastType} duration={4000} onClose={() => setToastMessage(null)} />
        )}
        <h1 className={styles.title}>Ranked Battles</h1>
        <div className={styles.titleDecor}>- Prove Your Worth -</div>

        {/* Rating overview cards */}
        <div className={styles.ratingOverview}>
          {['2v2', '3v3', '4v4'].map(bracket => {
            const rating = getRatingDisplay(bracket);
            return (
              <div key={bracket} className={styles.ratingCard}>
                <div className={styles.ratingBracket}>{bracket}</div>
                <div className={styles.ratingValue}>{rating ? Math.round(rating.rating) : '—'}</div>
                <div className={styles.ratingDetails}>
                  {rating ? `${rating.wins}W ${rating.losses}L` : 'Unranked'}
                </div>
              </div>
            );
          })}
          <div className={styles.dailyCounter}>
            <div className={styles.dailyCounterLabel}>Daily Battles</div>
            <div className={`${styles.dailyCounterValue} ${atDailyLimit ? styles.dailyLimitReached : ''}`}>
              {battlesToday} / {RANKED_DAILY_LIMIT}
            </div>
          </div>
        </div>

        {!meetsMinLevel && (
          <div className={styles.warningBanner}>
            Ranked battles require level {RANKED_MIN_LEVEL}. Your mercenary is level {mercenary?.level ?? 0}.
          </div>
        )}

        {atDailyLimit && (
          <div className={styles.warningBanner}>
            You have reached the daily ranked battle limit ({RANKED_DAILY_LIMIT}). Come back tomorrow!
          </div>
        )}

        <div className={styles.topBar}>
          {!userLobbyId ? (
            <button
              className={styles.createLobbyButton}
              onClick={() => navigate('/ranked-battle/create')}
              disabled={!meetsMinLevel || atDailyLimit}
            >
              ＋ Create Ranked Lobby
            </button>
          ) : (
            <button className={styles.leaveLobbyButton} onClick={handleLeaveLobby}>
              ✕ Leave Current Lobby
            </button>
          )}
          <div className={styles.filterBar}>
            <span className={styles.filterLabel}>Bracket:</span>
            {['all', '2v2', '3v3', '4v4'].map(b => (
              <button
                key={b}
                className={`${styles.filterButton} ${activeBracketFilter === b ? styles.activeFilter : ''}`}
                onClick={() => setActiveBracketFilter(b)}
              >
                {b === 'all' ? 'All' : b}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.sectionLabel}>Open Ranked Lobbies - {filteredLobbies.length} available</div>

        <div className={styles.lobbiesList}>
          {filteredLobbies.length === 0 ? (
            <p className={styles.noLobbies}>No ranked lobbies found. Be the first to challenge the arena.</p>
          ) : (
            filteredLobbies.map((lobby) => {
              const isFull = lobby.currentParticipants >= lobby.maxPlayers;
              const fillPct = Math.round((lobby.currentParticipants / lobby.maxPlayers) * 100);
              return (
                <div key={lobby.id}
                  className={`${styles.lobbyCard} ${lobby.id === userLobbyId ? styles.highlight : ''}`}
                  onClick={() => navigate(`/team-battle/lobby/${lobby.id}`)}>

                  <div className={styles.lobbyCardHeader}>
                    <h2 className={styles.lobbyName}>{lobby.name}</h2>
                    <div className={styles.lobbyBadges}>
                      {lobby.id === userLobbyId && <span className={`${styles.badge} ${styles.badgeMine}`}>Yours</span>}
                      <span className={`${styles.badge} ${styles.badgeRanked}`}>Ranked</span>
                      <span className={`${styles.badge} ${styles.badgeBracket}`}>{lobby.bracket}</span>
                    </div>
                  </div>

                  <div className={styles.lobbyMeta}>
                    <div className={styles.metaRow}>
                      <span className={styles.metaIcon}>⚔</span>
                      <span className={styles.metaLabel}>Levels</span>
                      <span className={styles.metaValue}>{lobby.minLevel}–{lobby.maxLevel}</span>
                    </div>
                    <div className={styles.metaRow}>
                      <span className={styles.metaIcon}>⚙</span>
                      <span className={styles.metaLabel}>Teams</span>
                      <span className={styles.metaValue}>{lobby.randomTeams ? 'Random' : 'Manual'}</span>
                    </div>
                  </div>

                  <div className={isFull ? `${styles.populationBar} ${styles.populationFull}` : styles.populationBar}>
                    <div className={styles.populationFill} style={{ width: `${fillPct}%` }} />
                  </div>
                  <div className={styles.populationText}>
                    {lobby.currentParticipants} / {lobby.maxPlayers} mercenaries
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </OffseasonNotice>
  );
};

export default RankedBattles;
